import { Tags } from "@/enum/tags";
import { Paginated } from "src/@types/paginated";
import { apiSlice } from "../http";

export interface IUpdateEmailSolicitationStatus {
  id: number;
  status: 'APPROVED' | 'REJECTED';
  review_notes?: string;
}

export interface IUpdateEmailSolicitationStatusBatch {
  items: IUpdateEmailSolicitationStatus[];
}

export interface IReviewEmailItemsIndividual {
  conciliation_email_id: number;
  items: Array<{
    item_type: 'DIVERGENCE' | 'NSU';
    item_id: number;
    review_id: number;
    review_notes: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
  }>;
}

export type IReviewEmailItemsBatch = IReviewEmailItemsIndividual[];

export interface IReviewResponseItem {
  review_id: number;
  item_type: 'DIVERGENCE' | 'NSU';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  assessment: string;
  reviewed_at: string;
  reviewed_by: string;
}

export interface IReviewResponseError {
  review_id: number;
  item_type: 'DIVERGENCE' | 'NSU';
  error: string;
}

export interface IReviewIndividualResponse {
  successful: IReviewResponseItem[];
  errors: IReviewResponseError[];
  summary: {
    email_id: number;
    total: number;
    successful: number;
    unsuccessful: number;
  };
}

export interface IReviewBatchEmailResponse {
  email_id: number;
  successful: IReviewResponseItem[];
  errors: IReviewResponseError[];
  summary: {
    email_id: number;
    total: number;
    successful: number;
    unsuccessful: number;
  };
}

export interface IReviewBatchResponse {
  successful: IReviewBatchEmailResponse[];
  errors: Array<{
    conciliation_email_id: number;
    error: string;
  }>;
  summary: {
    total: number;
    successful: number;
    unsuccessful: number;
  };
}

export interface IRecordEmailOpenedRequest {
  solicitation_id: number;
  is_rejected?: boolean;
}

export interface IRecordEmailOpenedResponse {
  recorded: number;
  solicitation_id: number;
  is_rejection_email: boolean;
}

export interface IResendEmailRequest {
  solicitation_id: number;
}

export interface IResendEmailResponse {
  success: boolean;
  solicitation_id: number;
  resent_items: {
    divergences: number;
    nsus: number;
  };
  message: string;
}

export type AggregatedStatus =
  | "PENDING"
  | "IN_ANALYSIS"
  | "AWAITING_STORE"
  | "RESPONDED"
  | "FINALIZED";

export interface IItemsSummary {
  total: number;
  pending_answer: number;
  responded: number;
  rejected_waiting: number;
  approved: number;
}

export interface IEmailDetailsDivergence {
  review_id: number;
  divergence_id: number;
  status: string;
  review_notes: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  name: string;
  amount: number;
  operator: string;
  store_justification: string | null;
  files: Array<{
    id: number;
    file_name: string;
    file_type: string;
    s3_path: string;
    entity_id: string;
  }>;
}

export interface IEmailDetailsNsu {
  review_id: number;
  nsu_id: number;
  status: string;
  review_notes: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  number: string;
  amount: number;
  operator: string;
  files: Array<{
    id: number;
    file_name: string;
    file_type: string;
    s3_path: string;
    entity_id: string;
  }>;
}

export interface IEmailDetailsResponse {
  id: number;
  store_code: number;
  regional_name: string | null;
  conciliation_id: number;
  type: string;
  requested_by_email: string;
  sent_at: string;
  closed_at: string | null;
  is_finalized: boolean;
  status: string;
  items_summary: IItemsSummary;
  aggregated_status: AggregatedStatus;
  divergences: IEmailDetailsDivergence[];
  nsus: IEmailDetailsNsu[];
}

export const emailReportInformation = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEmailReport: builder.query<Paginated<any>, any>({
      query: (filters) => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                params.append(key, String(item));
              });
            } else {
              params.append(key, String(value));
            }
          }
        });

        return {
          url: `/conciliation-email?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.EMAIL_REPORT],
    }),
    updateEmailSolicitationStatus: builder.mutation<any, IUpdateEmailSolicitationStatus | IUpdateEmailSolicitationStatusBatch>({
      query: (data: IUpdateEmailSolicitationStatus | IUpdateEmailSolicitationStatusBatch) => ({
        url: "/conciliation-email/status",
        method: "PATCH",
        body: 'items' in data ? data : { items: [data] },
      }),
      invalidatesTags: [Tags.EMAIL_REPORT],
    }),
    reviewEmailItemsIndividual: builder.mutation<IReviewIndividualResponse, IReviewEmailItemsIndividual>({
      query: (data) => ({
        url: "/conciliation-email/review-individual",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [Tags.EMAIL_REPORT, Tags.STORE_CONCILIATION],
    }),
    reviewEmailItemsBatch: builder.mutation<IReviewBatchResponse, IReviewEmailItemsBatch>({
      query: (data) => ({
        url: "/conciliation-email/review-batch",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [Tags.EMAIL_REPORT, Tags.STORE_CONCILIATION],
    }),
    recordEmailOpened: builder.mutation<IRecordEmailOpenedResponse, IRecordEmailOpenedRequest>({
      query: (data) => ({
        url: "/conciliation-email/record-opened",
        method: "POST",
        body: data,
      }),
    }),
    publicRecordEmailOpened: builder.mutation<IRecordEmailOpenedResponse, IRecordEmailOpenedRequest>({
      query: (data) => ({
        url: "/public/conciliation-email/record-opened",
        method: "POST",
        body: data,
      }),
    }),
    resendEmail: builder.mutation<IResendEmailResponse, IResendEmailRequest>({
      query: (data) => ({
        url: "/conciliation-email/resend",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [Tags.EMAIL_REPORT],
    }),
    getEmailDetails: builder.query<IEmailDetailsResponse, number>({
      query: (id) => ({
        url: `/conciliation-email/${id}/details`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: Tags.EMAIL_REPORT, id }],
    }),
    exportEmailReportExcel: builder.mutation<Blob, any>({
      query: (filters) => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                params.append(key, String(item));
              });
            } else {
              params.append(key, String(value));
            }
          }
        });

        return {
          url: `/conciliation-email/export-excel?${params.toString()}`,
          method: "POST",
          responseHandler: (response: { blob: () => any; }) => response.blob(),
        };
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetEmailReportQuery,
  useGetEmailDetailsQuery,
  useUpdateEmailSolicitationStatusMutation,
  useReviewEmailItemsIndividualMutation,
  useReviewEmailItemsBatchMutation,
  useExportEmailReportExcelMutation,
  useRecordEmailOpenedMutation,
  usePublicRecordEmailOpenedMutation,
  useResendEmailMutation,
} = emailReportInformation;

export const {
  endpoints: {
    getEmailReport: { select: selectGetEgetEmailReport },
  },
} = emailReportInformation;
